'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

interface MfaEnrollProps {
  onComplete: () => void;
  onSkip?: () => void;
}

export default function MfaEnroll({ onComplete, onSkip }: MfaEnrollProps) {
  const supabase = createClient();
  const [step, setStep] = useState<'loading' | 'enroll' | 'verify'>('loading');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    startEnrollment();
  }, []);

  const startEnrollment = async () => {
    setStep('loading');
    setError('');
    try {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Pretium+ Authenticator',
      });
      if (enrollError) throw enrollError;
      setFactorId(data.id);
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setStep('enroll');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to start MFA enrollment');
      setStep('enroll');
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit code from your authenticator app.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyError) throw verifyError;

      onComplete();
    } catch (err: any) {
      setError(err?.message ?? 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-blue/20 border border-blue/40 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-white">Set Up Two-Factor Authentication</h3>
        <p className="text-silver text-sm mt-1">Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
      </div>

      {qrCode && (
        <div className="flex flex-col items-center gap-3">
          <div className="bg-white p-3 rounded-lg">
            <img src={qrCode} alt="MFA QR Code" className="w-40 h-40" />
          </div>
          <div className="text-center">
            <p className="text-xs text-silver mb-1">Or enter this code manually:</p>
            <code className="text-xs font-mono bg-white/10 px-3 py-1.5 rounded text-blue tracking-widest break-all">
              {secret}
            </code>
          </div>
        </div>
      )}

      <form onSubmit={handleVerify} className="flex flex-col gap-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-silver mb-1.5">
            Verification Code
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            className="w-full bg-navy-mid border border-silver-dim rounded-lg px-4 py-3 text-white text-center text-xl tracking-[0.5em] font-mono placeholder-silver-dim focus:outline-none focus:border-blue focus:ring-1 focus:ring-blue/40 transition-all"
            autoComplete="one-time-code"
            required
          />
          <p className="text-xs text-silver/60 mt-1.5">Enter the 6-digit code shown in your authenticator app</p>
        </div>

        {error && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-alert/10 border border-red-alert/30">
            <svg className="w-4 h-4 text-red-alert flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <p className="text-red-alert text-xs">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || code.length !== 6}
          className="btn-electric w-full py-3 rounded-lg text-sm font-semibold tracking-wide uppercase disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Verifying…
            </>
          ) : (
            'Verify & Enable MFA'
          )}
        </button>
      </form>
    </div>
  );
}
